import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import {
  APPLY_PRICING_URL,
  applyPricingToSubscriptions,
  createPricingVersion,
  deletePricingVersion,
  describeError,
  ESSENTIAL_PLAN_CODE,
  getPlatformPlans,
  getPricing,
  TRIAL_PLAN_CODE,
  type PlatformPlan,
  type PricingApplyItem,
  type PricingApplyResponse,
  type PricingResponse,
  type PricingVersionDto,
} from '@/lib/api';
import { useCurrencyField } from '@vaptmenu/money';
import { useAuthStore } from '@/stores/auth';
import { useIdempotencyKey } from '@/lib/idempotency';
import { freeMonths } from '@/lib/pricing-math';
import { stepUpOptions, useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { AiUnitPacksPanel } from '@/components/AiUnitPacksPanel';
import { StepUpDialog } from '@/components/StepUpDialog';
import { Badge, Tabs } from '@/components/ui';

/**
 * Planos e preços (change platform-pricing-config; por plano desde a change
 * billing-plans).
 *
 * Uma aba por plano vendável (todos menos o teste grátis), com o selo "Fora da
 * venda" no plano que ainda não é público. Em cada aba, dois números e a cota
 * decidem o que se cobra de quem assina aquele plano a partir de agora; a
 * página deixa isso visível e barato de mudar — e caro de errar: os derivados
 * (equivalente mensal do anual, meses grátis) são calculados, nunca digitados;
 * criar uma versão pede confirmação explicando o que muda e o que NÃO muda; e
 * reprecificar quem já assina é uma ação separada, só daquele plano, que só se
 * habilita depois do dry-run mostrar a lista de quem seria tocado.
 *
 * Operador sem papel de Admin vê tudo em modo leitura. Contra uma API que
 * ainda não expõe o catálogo de planos (404 em `/platform/plans`), a página
 * mostra só o Essencial, como antes dos planos.
 *
 * A aba "Pacotes" (change billing-ai-units) fica depois dos planos e só
 * aparece para Admin: os pacotes de atendimentos têm preço, quantidade e
 * validade por versão, como o plano, e a rota deles é só de Admin. Ela vai no
 * endereço como `?aba=pacotes`; quem não é Admin e abre esse endereço cai na
 * aba do plano.
 */

/** Valor interno da aba de pacotes na lista de abas; não é código de plano (que não tem dois-pontos). */
const PACKS_TAB = 'aba:pacotes';
/** Valor de `?aba=` da aba de pacotes. */
const PACKS_TAB_PARAM = 'pacotes';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dateTime = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const integer = new Intl.NumberFormat('pt-BR');

/** Teto da cota de atendimentos inclusos numa versão (o mesmo da API). */
const MAX_INCLUDED_AI_UNITS = 100_000;

/** Motivo que a API dá ao item que já está no preço vigente. */
const ALREADY_AT_PRICE_REASON = 'ja esta no preco vigente';

function aiUnitsLabel(units: number | undefined): string {
  return `${integer.format(units ?? 0)} por mês`;
}

function subscriptionsLabel(count: number): string {
  return count === 1 ? '1 assinatura ativa' : `${integer.format(count)} assinaturas ativas`;
}

/** O catálogo: carregando, indisponível nesta API (`legacy`) ou com os planos. */
type Catalog =
  | { state: 'loading' }
  | { state: 'legacy'; warning: string | null }
  | { state: 'ready'; plans: PlatformPlan[] };

export function PricingPage() {
  const role = useAuthStore((s) => s.operator?.role ?? null);
  const isAdmin = role === 'Admin';

  const [catalog, setCatalog] = useState<Catalog>({ state: 'loading' });
  const [catalogTick, setCatalogTick] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const reloadCatalog = useCallback(() => setCatalogTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    getPlatformPlans()
      .then((plans) => {
        if (cancelled) return;
        setCatalog(plans === null ? { state: 'legacy', warning: null } : { state: 'ready', plans });
      })
      .catch((err) => {
        // Sem o catálogo, o Essencial continua editável pela rota de preços, que
        // existe desde antes dos planos: melhor a visão de um plano do que nada.
        if (cancelled) return;
        setCatalog({
          state: 'legacy',
          warning: `Não foi possível carregar os planos (${describeError(err)}). Mostrando só o Essencial.`,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogTick]);

  const sellable = useMemo(
    () =>
      catalog.state === 'ready'
        ? [...catalog.plans].filter((p) => p.code !== TRIAL_PLAN_CODE).sort((a, b) => a.sortOrder - b.sortOrder)
        : [],
    [catalog],
  );

  const requested = searchParams.get('plano');
  const selected =
    sellable.find((p) => p.code === requested) ??
    sellable.find((p) => p.code === ESSENTIAL_PLAN_CODE) ??
    sellable[0] ??
    null;

  // A aba de pacotes só existe para Admin e com o catálogo de planos (a API de antes dos planos
  // também não tem os pacotes).
  const showPacks = isAdmin && catalog.state === 'ready';
  const packsSelected = showPacks && searchParams.get('aba') === PACKS_TAB_PARAM;

  function selectTab(value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === PACKS_TAB) {
          next.set('aba', PACKS_TAB_PARAM);
        } else {
          next.delete('aba');
          next.set('plano', value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const tabs = [
    ...sellable.map((p) => ({
      value: p.code,
      label: (
        <>
          {p.name}
          {!p.isPublic && <Badge tone="warning">Fora da venda</Badge>}
        </>
      ),
    })),
    ...(showPacks ? [{ value: PACKS_TAB, label: 'Pacotes' }] : []),
  ];
  const activeTab = packsSelected || (!selected && showPacks) ? PACKS_TAB : (selected?.code ?? null);

  return (
    <div className="page">
      <h1 style={{ marginTop: 0 }}>Planos e preços</h1>

      {catalog.state === 'loading' && <p className="muted">Carregando…</p>}

      {catalog.state === 'legacy' && (
        <>
          {catalog.warning && (
            <div className="alert" role="status" style={{ marginBottom: 16 }}>
              {catalog.warning}
            </div>
          )}
          <PlanPricingPanel planCode={ESSENTIAL_PLAN_CODE} plan={null} isAdmin={isAdmin} onChanged={reloadCatalog} />
        </>
      )}

      {catalog.state === 'ready' && tabs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Tabs ariaLabel="Planos" value={activeTab ?? ''} onChange={selectTab} tabs={tabs} />
        </div>
      )}

      {catalog.state === 'ready' && activeTab === PACKS_TAB && <AiUnitPacksPanel />}

      {catalog.state === 'ready' && activeTab !== PACKS_TAB && selected && (
        // key: trocar de aba recomeça o formulário e o dry-run, que são de um plano só.
        <PlanPricingPanel
          key={selected.code}
          planCode={selected.code}
          plan={selected}
          isAdmin={isAdmin}
          onChanged={reloadCatalog}
        />
      )}

      {catalog.state === 'ready' && !selected && activeTab !== PACKS_TAB && (
        <p className="muted">Nenhum plano vendável no catálogo.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Preço de um plano: vigente, nova versão, aplicação e histórico. `plan` nulo
 * é a visão de antes dos planos (API sem catálogo): só o Essencial, sem cota.
 */
function PlanPricingPanel({
  planCode,
  plan,
  isAdmin,
  onChanged,
}: {
  planCode: string;
  plan: PlatformPlan | null;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const legacy = plan === null;
  const archived = plan?.archivedAt != null;

  const reload = useCallback(() => {
    setReloadTick((t) => t + 1);
    onChanged();
  }, [onChanged]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    getPricing(legacy ? undefined : planCode)
      .then((data) => !cancelled && setPricing(data))
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [planCode, legacy, reloadTick]);

  return (
    <section aria-label={plan ? `Preço do plano ${plan.name}` : 'Preço do plano'}>
      {plan && <PlanHeader plan={plan} />}

      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {loading && !pricing && <p className="muted">Carregando…</p>}

      {pricing && (
        <>
          <CurrentCard
            current={pricing.current}
            scheduled={pricing.scheduled}
            showQuota={!legacy}
            isAdmin={isAdmin}
            onChanged={reload}
          />
          {isAdmin && !archived && (
            <NewVersionCard
              // Remonta depois de cada versão: a cota parte da vigente de novo.
              key={pricing.current?.id ?? 'none'}
              planCode={planCode}
              plan={plan}
              initialQuota={pricing.current?.includedAiUnitsPerMonth ?? 0}
              onCreated={reload}
            />
          )}
          {/* Plano arquivado não tem vigente para a API: nem versão nova nem aplicação. */}
          {isAdmin && !archived && (
            <ApplyCard planCode={planCode} planName={plan?.name ?? null} current={pricing.current} />
          )}
          <HistoryCard history={pricing.history} showQuota={!legacy} />
        </>
      )}
    </section>
  );
}

function PlanHeader({ plan }: { plan: PlatformPlan }) {
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="plan-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>{plan.name}</h2>
        {plan.isPublic ? <Badge tone="success">À venda</Badge> : <Badge tone="warning">Fora da venda</Badge>}
        {plan.archivedAt && <Badge tone="neutral">Arquivado</Badge>}
      </div>
      {plan.description && (
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>
          {plan.description}
        </p>
      )}
      <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="plan-counts">
        {subscriptionsLabel(plan.currentSubscriptions)} · {integer.format(plan.activeOverrides)}{' '}
        {plan.activeOverrides === 1 ? 'cortesia vigente' : 'cortesias vigentes'}
        {!plan.isPublic && ' · não aparece para quem assina nem para quem muda de plano'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CurrentCard({
  current,
  scheduled,
  showQuota,
  isAdmin,
  onChanged,
}: {
  current: PricingVersionDto | null;
  scheduled: PricingVersionDto | null;
  showQuota: boolean;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  async function cancelScheduled() {
    if (!scheduled) return;
    if (!confirm(`Cancelar a versão agendada para ${dateTime.format(new Date(scheduled.effectiveFrom))}?`)) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await deletePricingVersion(scheduled.id);
      onChanged();
    } catch (err) {
      setCancelError(describeError(err));
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Vigente</h2>
      {current ? (
        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: 0 }}>
          <Stat label="Mensal" value={brl.format(current.monthlyPrice)} testId="current-monthly" />
          <Stat label="Anual" value={brl.format(current.yearlyPrice)} testId="current-yearly" />
          <Stat
            label="Equivalente por mês no anual"
            value={`${brl.format(current.yearlyPrice / 12)}/mês`}
            testId="current-yearly-per-month"
          />
          <Stat
            label="Meses grátis no anual"
            value={String(freeMonths(current.monthlyPrice, current.yearlyPrice))}
            testId="current-free-months"
          />
          {showQuota && (
            <Stat
              label="Atendimentos inclusos"
              value={aiUnitsLabel(current.includedAiUnitsPerMonth)}
              testId="current-ai-units"
            />
          )}
          <Stat label="Vigente desde" value={dateTime.format(new Date(current.effectiveFrom))} />
        </dl>
      ) : (
        <p className="muted">
          Nenhuma versão em vigor. No Essencial, a API usa o preço do arquivo de configuração; nos outros planos, o
          plano fica sem preço e não pode ser vendido. Crie uma versão para assumir o controle.
        </p>
      )}

      {scheduled && (
        <div className="alert" style={{ marginTop: 12 }} data-testid="scheduled">
          <strong>Agendada:</strong> {brl.format(scheduled.monthlyPrice)} / {brl.format(scheduled.yearlyPrice)}
          {showQuota && ` · ${aiUnitsLabel(scheduled.includedAiUnitsPerMonth)}`} a partir de{' '}
          {dateTime.format(new Date(scheduled.effectiveFrom))}
          {scheduled.note && <span className="muted"> — {scheduled.note}</span>}
          {isAdmin && (
            <button type="button" className="btn" style={{ marginLeft: 12, padding: '4px 12px', fontSize: 13 }}
              onClick={cancelScheduled} disabled={cancelling}>
              Cancelar agendamento
            </button>
          )}
          {cancelError && <div className="alert alert-error" role="alert">{cancelError}</div>}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div>
      <dt className="muted" style={{ fontSize: 12 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 18, fontWeight: 600 }} data-testid={testId}>{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewVersionCard({
  planCode,
  plan,
  initialQuota,
  onCreated,
}: {
  planCode: string;
  plan: PlatformPlan | null;
  initialQuota: number;
  onCreated: () => void;
}) {
  // Número, não texto: o campo monetário de `@vaptmenu/money` faz a leitura e
  // devolve o valor já resolvido. Enquanto esta tela guardava texto e tinha
  // parser próprio, `39.90` virava 3990 — e ninguém via, porque o campo não
  // mostrava o que o sistema tinha entendido.
  const [monthly, setMonthly] = useState<number | null>(null);
  const [yearly, setYearly] = useState<number | null>(null);
  // A cota parte da vigente: mudar o preço do plano com atendente não pode
  // zerar os atendimentos inclusos por descuido.
  const [quota, setQuota] = useState(String(initialQuota));
  const [when, setWhen] = useState<'now' | 'scheduled'>('now');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const legacy = plan === null;

  // Máscara: os dígitos preenchem os centavos, o campo tem sempre duas casas
  // e não há separador para digitar — `39.90` virar 3990 deixa de ser possível.
  const monthlyField = useCurrencyField({ value: monthly, onValueChange: setMonthly });
  const yearlyField = useCurrencyField({ value: yearly, onValueChange: setYearly });

  const parsed = useMemo(
    () => (monthly !== null && yearly !== null ? { monthly, yearly } : null),
    [monthly, yearly],
  );
  const parsedQuota = /^\d+$/.test(quota.trim()) ? Number(quota.trim()) : null;

  const localError = useMemo(() => {
    if (!parsed) return null;
    if (parsed.monthly <= 0 || parsed.yearly <= 0) return 'Os dois valores precisam ser positivos.';
    if (parsed.yearly > parsed.monthly * 12) return 'O anual não pode custar mais que 12 mensais.';
    if (!legacy && (parsedQuota === null || parsedQuota > MAX_INCLUDED_AI_UNITS)) {
      return 'A cota de atendimentos deve ser um número inteiro de 0 a 100.000.';
    }
    if (when === 'scheduled' && !effectiveFrom) return 'Informe a data de vigência.';
    return null;
  }, [parsed, legacy, parsedQuota, when, effectiveFrom]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!parsed || localError) return;

    const whenText = when === 'now'
      ? 'a partir de agora'
      : `a partir de ${dateTime.format(new Date(effectiveFrom))}`;
    const values = `${brl.format(parsed.monthly)}/mês ou ${brl.format(parsed.yearly)}/ano`;
    const message = plan
      ? `Novas assinaturas do ${plan.name} passam a usar ${values}, com ${integer.format(parsedQuota ?? 0)} ` +
        `atendimentos inclusos por mês, ${whenText}.\n\n` +
        `${plan.currentSubscriptions === 1 ? 'A' : 'As'} ${subscriptionsLabel(plan.currentSubscriptions)} do ` +
        `${plan.name} ${plan.currentSubscriptions === 1 ? 'continua' : 'continuam'} no valor atual até você usar ` +
        '"Aplicar às assinaturas ativas".' +
        (plan.isPublic ? '' : '\n\nO plano está fora da venda: ninguém novo assina enquanto ele não for publicado.') +
        '\n\nConfirmar?'
      : `Novas assinaturas passam a usar ${values} ${whenText}.\n\n` +
        'Quem JÁ assina continua no valor atual até você usar "Aplicar às assinaturas ativas".\n\nConfirmar?';
    if (!confirm(message)) return;

    setSubmitting(true);
    setError(null);
    try {
      await createPricingVersion({
        planCode,
        monthlyPrice: parsed.monthly,
        yearlyPrice: parsed.yearly,
        // A API de antes dos planos não conhece a cota: na visão de um plano só, ela não vai.
        ...(legacy ? {} : { includedAiUnitsPerMonth: parsedQuota ?? 0 }),
        effectiveFrom: when === 'scheduled' ? new Date(effectiveFrom).toISOString() : null,
        note: note.trim() || null,
      });
      setMonthly(null);
      setYearly(null);
      setNote('');
      setWhen('now');
      setEffectiveFrom('');
      onCreated();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="card" style={{ marginBottom: 16 }} onSubmit={submit} aria-label="Nova versão de preço">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Nova versão</h2>
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pr-monthly">Mensal (R$)</label>
          <input id="pr-monthly" placeholder="39,90" {...monthlyField} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pr-yearly">Anual (R$)</label>
          <input id="pr-yearly" placeholder="399,00" {...yearlyField} />
        </div>
        {!legacy && (
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="pr-quota">Atendimentos inclusos por mês</label>
            <input
              id="pr-quota"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_INCLUDED_AI_UNITS}
              step={1}
              value={quota}
              onChange={(e) => setQuota(e.target.value)}
            />
          </div>
        )}
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pr-when">Vigência</label>
          <select id="pr-when" value={when} onChange={(e) => setWhen(e.target.value as 'now' | 'scheduled')}>
            <option value="now">Agora</option>
            <option value="scheduled">Agendar</option>
          </select>
        </div>
        {when === 'scheduled' && (
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="pr-effective">A partir de</label>
            <input id="pr-effective" type="datetime-local" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
        )}
        <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
          <label htmlFor="pr-note">Nota (opcional)</label>
          <input id="pr-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="ex.: anúncio enviado em 01/10" />
        </div>
      </div>

      {parsed && !localError && (
        <p className="muted" style={{ marginBottom: 0 }} data-testid="preview">
          Equivale a {brl.format(parsed.yearly / 12)}/mês no anual · {freeMonths(parsed.monthly, parsed.yearly)} meses grátis
        </p>
      )}
      {localError && <p className="muted" role="status">{localError}</p>}
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div style={{ marginTop: 12 }}>
        <button type="submit" className="btn" disabled={!parsed || !!localError || submitting}>
          Salvar versão
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

/** Item que aparece na tabela: o que muda, falhou ou foi pulado por outro motivo que o preço. */
function isShown(item: PricingApplyItem): boolean {
  return item.outcome !== 'skipped' || (item.reason !== null && item.reason !== ALREADY_AT_PRICE_REASON);
}

function ApplyCard({
  planCode,
  planName,
  current,
}: {
  planCode: string;
  planName: string | null;
  current: PricingVersionDto | null;
}) {
  const [preview, setPreview] = useState<PricingApplyResponse | null>(null);
  const [result, setResult] = useState<PricingApplyResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  // O ensaio não pede step-up nem abre modal: nada é tocado.
  async function runDryRun() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setPreview(await applyPricingToSubscriptions(planCode, true));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * A aplicação de verdade, com o código do step-up (C6). A chave é a da tentativa: a repetição
   * depois do 403 de step-up sai com a mesma (`useIdempotencyKey`). O plano é o que estava na tela
   * ao abrir o modal (`startApply`), congelado pelo `useStepUpDialog`.
   */
  async function apply(proof: StepUpProof, confirmedPlanCode: string) {
    const body = { planCode: confirmedPlanCode, dryRun: false };
    const key = idempotency.keyFor(APPLY_PRICING_URL, body);
    let r;
    try {
      r = await applyPricingToSubscriptions(confirmedPlanCode, false, { idempotencyKey: key, ...stepUpOptions(proof) });
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    setResult(r);
    setPreview(null);
  }

  const stepUp = useStepUpDialog(apply, (err) => setError(describeError(err)), {
    // O plano confirmado ainda é o da aba e o ensaio continua na tela.
    isValid: (confirmedPlanCode) => confirmedPlanCode === planCode && preview !== null,
    // Passkey (C14): o desafio preso ao plano confirmado.
    passkeyTarget: (confirmedPlanCode) => stepUpTarget('pricingApply', confirmedPlanCode),
  });

  function startApply() {
    if (!preview || stepUp.open) return;
    setError(null);
    stepUp.start(planCode);
  }

  const planned = preview?.items.filter((i) => i.outcome === 'planned') ?? [];
  const skippedForChange = preview?.items.filter((i) => i.outcome === 'skipped' && isShown(i)).length ?? 0;
  const target = planName ? ` do ${planName}` : '';

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Aplicar às assinaturas ativas</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Mudar o preço não muda quem já assina. Esta ação leva o preço vigente
        {planName ? ` do ${planName}` : ''}
        {current ? ` (${brl.format(current.monthlyPrice)} / ${brl.format(current.yearlyPrice)})` : ''} para as assinaturas
        ativas {planName ? 'desse plano ' : ''}que estão em outro valor — primeiro mostrando quem seria tocado. Assinaturas
        com mudança de plano em andamento ficam de fora.
      </p>

      <div className="btn-row">
        {/* Travados com o modal do step-up aberto: um novo ensaio trocaria a simulação que o
            modal descreve. */}
        <button type="button" className="btn" onClick={runDryRun} disabled={busy || stepUp.open || !current}>
          Simular (dry-run)
        </button>
        <button type="button" className="btn btn-primary" onClick={startApply}
          disabled={busy || stepUp.open || !preview || planned.length === 0}
          data-testid="apply-confirm">
          Aplicar {preview ? `(${planned.length})` : ''}
        </button>
      </div>

      {error && <div className="alert alert-error" role="alert" style={{ marginTop: 12 }}>{error}</div>}

      {stepUp.open && (
        <StepUpDialog
          title="Aplicar preço às assinaturas"
          description={
            <p style={{ margin: 0 }}>
              Reprecificar {planned.length} assinatura(s){target} no Asaas para o preço vigente? Isso não pode ser
              desfeito em lote.
            </p>
          }
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}

      {preview && (
        <ApplyTable
          title={
            `Simulação: ${planned.length} seriam atualizadas, ${preview.skipped - skippedForChange} já estão no preço` +
            (skippedForChange > 0 ? `, ${skippedForChange} puladas por mudança de plano em andamento` : '')
          }
          data={preview}
        />
      )}
      {result && (
        <ApplyTable
          title={`Resultado: ${result.updated} atualizadas, ${result.skipped} já estavam no preço ou foram puladas, ${result.failed} falharam`}
          data={result}
        />
      )}
    </div>
  );
}

function ApplyTable({ title, data }: { title: string; data: PricingApplyResponse }) {
  const rows = data.items.filter(isShown);
  return (
    <div style={{ marginTop: 12 }}>
      <p style={{ margin: '0 0 8px' }}><strong>{title}</strong></p>
      {rows.length === 0 ? (
        <p className="muted">Nenhuma assinatura precisa mudar.</p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Assinaturas afetadas">
            <THead>
              <Tr><Th>Assinatura</Th><Th>Ciclo</Th><Th>Atual</Th><Th>Novo</Th><Th>Desconto ativo</Th><Th>Situação</Th></Tr>
            </THead>
            <TBody>
              {rows.map((i) => (
                <Tr key={i.subscriptionId}>
                  <Td heading style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{i.subscriptionId.slice(0, 8)}…</Td>
                  <Td label="Ciclo">{i.billingCycle === 'yearly' ? 'Anual' : 'Mensal'}</Td>
                  <Td label="Atual">{brl.format(i.currentValue)}</Td>
                  <Td label="Novo">{brl.format(i.newValue)}</Td>
                  <Td label="Desconto ativo">{i.hasActiveDiscount ? <span className="badge">Sim</span> : <span className="muted">—</span>}</Td>
                  <Td label="Situação">
                    {i.outcome === 'failed'
                      ? <span className="badge badge-error" title={i.reason ?? ''}>Falhou</span>
                      : i.outcome === 'updated'
                        ? <span className="badge badge-success">Atualizada</span>
                        : i.outcome === 'skipped'
                          ? <span className="badge badge-warning">Pulada</span>
                          : <span className="badge">Será atualizada</span>}
                    {(i.outcome === 'failed' || i.outcome === 'skipped') && i.reason && (
                      <div className="muted" style={{ fontSize: 12 }}>{i.reason}</div>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function HistoryCard({ history, showQuota }: { history: PricingVersionDto[]; showQuota: boolean }) {
  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Histórico</h2>
      {history.length === 0 ? (
        <p className="muted">Nenhuma versão registrada.</p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Histórico de versões">
            <THead>
              <Tr>
                <Th>Vigência</Th><Th>Mensal</Th><Th>Anual</Th>
                {showQuota && <Th>Atendimentos</Th>}
                <Th>Nota</Th><Th>Criada em</Th>
              </Tr>
            </THead>
            <TBody>
              {history.map((v) => (
                <Tr key={v.id}>
                  <Td heading>{dateTime.format(new Date(v.effectiveFrom))}</Td>
                  <Td label="Mensal">{brl.format(v.monthlyPrice)}</Td>
                  <Td label="Anual">{brl.format(v.yearlyPrice)}</Td>
                  {showQuota && <Td label="Atendimentos">{aiUnitsLabel(v.includedAiUnitsPerMonth)}</Td>}
                  <Td label="Nota" className="muted">{v.note ?? '—'}</Td>
                  <Td label="Criada em" className="muted">{dateTime.format(new Date(v.createdAt))}</Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}
